import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Avatar, Box, Button, Card, Chip, CircularProgress, Drawer, FormControlLabel, IconButton,
  MenuItem, Select, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import paperPlaneFill from '@iconify/icons-eva/paper-plane-fill';
import editFill from '@iconify/icons-eva/edit-fill';
import emailFill from '@iconify/icons-eva/email-fill';
import linkedinFill from '@iconify/icons-eva/linkedin-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import flashFill from '@iconify/icons-eva/flash-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import arrowBackFill from '@iconify/icons-eva/arrow-back-fill';
import checkmarkCircle from '@iconify/icons-eva/checkmark-circle-2-fill';
import {
  previewEligibility, listOutlookAccounts, listUnipileAccounts, listMessageTemplates,
  createCampaign, activateCampaign, parseRulesFromText, getFilterOptions,
} from '../../utils/outreachApi';

// ──────────────────────────────────────────────────────────────────
// STEP DEFINITIONS — the conversation flow
// ──────────────────────────────────────────────────────────────────
// Country and industry options are loaded dynamically from the backend
// so they always match the actual labels stored in the DB.
const FALLBACK_COUNTRY_OPTIONS  = ['United States', 'United Kingdom', 'Canada', 'India', 'Germany'];
const FALLBACK_INDUSTRY_OPTIONS = ['SaaS', 'Fintech', 'BFSI', 'Healthcare', 'Manufacturing'];
const SIZE_OPTIONS = [
  { k: '1-50',    label: 'Startup (1–50)',         range: [1, 50]    },
  { k: '50-200',  label: 'SMB (50–200)',           range: [50, 200]  },
  { k: '200-500', label: 'Mid-market (200–500)',   range: [200, 500] },
  { k: '500+',    label: 'Enterprise (500+)',      range: [500, 100000] },
];

// Optional filters that the user can stack via the "Anything else?" step.
const EXTRA_FILTERS = [
  {
    k: 'revenue', label: 'Revenue range', chipLabel: 'Revenue',
    q: 'What revenue range?',
    options: [
      { k: 'lt_1m',  label: '< $1M',          rule: { field: 'revenue', operator: 'lte',     value: 1000000 } },
      { k: '1_10m',  label: '$1M – $10M',     rule: { field: 'revenue', operator: 'between', value: [1000000, 10000000] } },
      { k: '10_50m', label: '$10M – $50M',    rule: { field: 'revenue', operator: 'between', value: [10000000, 50000000] } },
      { k: 'gt_50m', label: '> $50M',         rule: { field: 'revenue', operator: 'gte',     value: 50000000 } },
    ],
  },
  {
    k: 'foundation_year', label: 'Founded recently', chipLabel: 'Founded',
    q: 'Founded after which year?',
    options: [
      { k: 'y_2020', label: 'After 2020', rule: { field: 'foundation_year', operator: 'gte', value: 2020 } },
      { k: 'y_2015', label: 'After 2015', rule: { field: 'foundation_year', operator: 'gte', value: 2015 } },
      { k: 'y_2010', label: 'After 2010', rule: { field: 'foundation_year', operator: 'gte', value: 2010 } },
    ],
  },
  {
    k: 'has_email', label: 'Has email contact', chipLabel: 'Email',
    q: 'Only companies with an email contact?',
    options: [
      { k: 'yes', label: 'Yes — only with email', rule: { field: 'has_email', operator: 'eq', value: true } },
    ],
  },
  {
    k: 'has_linkedin', label: 'Has LinkedIn URL', chipLabel: 'LinkedIn',
    q: 'Only companies with a LinkedIn URL?',
    options: [
      { k: 'yes', label: 'Yes — only with LinkedIn', rule: { field: 'has_linkedin', operator: 'eq', value: true } },
    ],
  },
  {
    k: 'confidence', label: 'Confidence level', chipLabel: 'Confidence',
    q: 'Which confidence levels?',
    options: [
      { k: 'high',   label: 'High only',    rule: { field: 'confidence_level', operator: 'eq', value: 'High' } },
      { k: 'med+',   label: 'Medium+',      rule: { field: 'confidence_level', operator: 'in', value: ['Medium', 'High'] } },
    ],
  },
  {
    k: 'company_type', label: 'Company type', chipLabel: 'Type',
    q: 'Which company types?',
    options: [
      { k: 'private', label: 'Private',  rule: { field: 'company_type', operator: 'eq', value: 'Private' } },
      { k: 'public',  label: 'Public',   rule: { field: 'company_type', operator: 'eq', value: 'Public' } },
      { k: 'startup', label: 'Startup',  rule: { field: 'company_type', operator: 'eq', value: 'Startup' } },
    ],
  },
];
const SEQUENCE_PRESETS = [
  { k: 'email_only',    label: 'Email only',                 description: '1 step · just an email',
    steps: [
      { channel: 'EMAIL', message_template_id: 7, delay_seconds: 0 },
    ],
  },
  { k: 'email_li',      label: 'Email + LinkedIn nudge',     description: '2 steps · email then a LinkedIn invite 2 days later',
    steps: [
      { channel: 'EMAIL',           message_template_id: 7,  delay_seconds: 0      },
      { channel: 'LINKEDIN_INVITE', message_template_id: 11, delay_seconds: 172800 },
    ],
  },
  { k: 'full',          label: 'Full sequence',              description: '3 steps · email → LinkedIn invite → email follow-up',
    steps: [
      { channel: 'EMAIL',           message_template_id: 7,  delay_seconds: 0      },
      { channel: 'LINKEDIN_INVITE', message_template_id: 11, delay_seconds: 172800 },
      { channel: 'EMAIL',           message_template_id: 9,  delay_seconds: 432000 },
    ],
  },
  { k: 'li_first',      label: 'LinkedIn-first',             description: '2 steps · LinkedIn invite, then email if connected',
    steps: [
      { channel: 'LINKEDIN_INVITE', message_template_id: 11, delay_seconds: 0      },
      { channel: 'EMAIL',           message_template_id: 7,  delay_seconds: 172800 },
    ],
  },
];
const SCHEDULE_PRESETS = [
  { k: 'biz_ist', label: 'Standard biz hours · IST', schedule: { window_start: '09:00', window_end: '18:00', timezone: 'Asia/Kolkata',     weekdays: ['MON','TUE','WED','THU','FRI'], per_pm_daily_cap: 50 } },
  { k: 'biz_pst', label: 'US business · PST',        schedule: { window_start: '09:00', window_end: '18:00', timezone: 'America/Los_Angeles', weekdays: ['MON','TUE','WED','THU','FRI'], per_pm_daily_cap: 50 } },
  { k: 'biz_gmt', label: 'UK business · GMT',        schedule: { window_start: '09:00', window_end: '18:00', timezone: 'Europe/London',      weekdays: ['MON','TUE','WED','THU','FRI'], per_pm_daily_cap: 50 } },
  { k: 'soft',    label: 'Light cadence · 25/day',   schedule: { window_start: '10:00', window_end: '17:00', timezone: 'Asia/Kolkata',      weekdays: ['MON','TUE','WED','THU','FRI'], per_pm_daily_cap: 25 } },
];

const STEPS = [
  { id: 'name',      type: 'text',         q: "Hi! Let's set up an outreach campaign. What should we call it?",
    suggestions: ['SaaS Q3 outbound', 'Fintech UK push', 'D2C reactivation'] },
  { id: 'industry',  type: 'chips-multi',  q: 'Which industries do you want to target?',
    options: FALLBACK_INDUSTRY_OPTIONS, allowSkip: true, skipLabel: 'Any industry' },
  { id: 'country',   type: 'chips-multi',  q: 'Which countries?',
    options: FALLBACK_COUNTRY_OPTIONS, allowSkip: true, skipLabel: 'Any country' },
  { id: 'size',      type: 'size-picker',  q: 'What size companies?' },
  { id: 'more',      type: 'more-filters', q: 'Want to add any more filters? You can stack as many as you like.' },
  { id: 'sequence',  type: 'preset-card',  q: 'How should we reach them?',
    options: SEQUENCE_PRESETS },
  { id: 'customize', type: 'customize',    q: 'Want to swap templates or change wait times? Defaults work fine if not.' },
  { id: 'pms',       type: 'chips-multi-rich', q: 'Whose Outlook will send the emails?' },
  { id: 'linkedin',  type: 'chips-multi-rich', q: 'Pick one or more LinkedIn profiles to send invites/messages from.',
    onlyIfLinkedinSequence: true },
  { id: 'schedule',  type: 'schedule',     q: 'When should we send?' },
  { id: 'review',    type: 'review',       q: "Here's what I have — ready to launch?" },
];

// ──────────────────────────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────────────────────────
const buildRules = (answers) => {
  const rules = [];
  if (answers.industry?.length) rules.push({ group: 1, field: 'industry', operator: 'in', value: answers.industry });
  if (answers.country?.length)  rules.push({ group: 1, field: 'country',  operator: 'in', value: answers.country });
  if (answers.size) {
    if (answers.size?.custom) {
      const { min, max } = answers.size;
      const lo = Number(min) || 0;
      const hi = Number(max) || 100000;
      rules.push({ group: 1, field: 'fte', operator: 'between', value: [lo, hi] });
    } else {
      const meta = SIZE_OPTIONS.find((s) => s.k === answers.size);
      if (meta) rules.push({ group: 1, field: 'fte', operator: 'between', value: meta.range });
    }
  }
  (answers.extra_filters || []).forEach((f) => {
    if (f.rule) rules.push({ group: 1, field: f.rule.field, operator: f.rule.operator, value: f.rule.value });
  });
  return rules;
};

const summarizeAnswer = (step, answer) => {
  if (step.id === 'more') {
    if (!answer || answer.length === 0) return 'no extra filters';
    return `${answer.length} extra filter${answer.length > 1 ? 's' : ''}`;
  }
  if (!answer || (Array.isArray(answer) && answer.length === 0)) return '— skipped —';
  if (step.id === 'sequence') return SEQUENCE_PRESETS.find((s) => s.k === answer)?.label;
  if (step.id === 'schedule') return SCHEDULE_PRESETS.find((s) => s.k === answer)?.label;
  if (step.id === 'size') {
    if (!answer) return 'any size';
    if (answer?.custom) return `${answer.min || 0}–${answer.max || '∞'} employees`;
    return SIZE_OPTIONS.find((s) => s.k === answer)?.label;
  }
  if (Array.isArray(answer))  return answer.join(', ');
  return String(answer);
};

// ──────────────────────────────────────────────────────────────────
// BUBBLES
// ──────────────────────────────────────────────────────────────────
const BotBubble = ({ children }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 2.25 }}>
    <Avatar sx={{
      width: 36, height: 36,
      bgcolor: 'primary.main', color: 'primary.contrastText',
      boxShadow: '0 2px 6px rgba(25,118,210,0.25)',
    }}>
      <Icon icon={flashFill} width={20} />
    </Avatar>
    <Box sx={{
      bgcolor: 'background.paper', borderRadius: 2, p: 1.75,
      maxWidth: '85%',
      borderTopLeftRadius: 4,
      border: 1, borderColor: 'grey.200',
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    }}>
      <Typography variant="body2" sx={{ lineHeight: 1.55 }}>{children}</Typography>
    </Box>
  </Stack>
);

const UserBubble = ({ children, onEdit }) => (
  <Stack direction="row" spacing={1} sx={{ mb: 2.25, justifyContent: 'flex-end' }}>
    <Box sx={{
      bgcolor: 'primary.main', color: 'primary.contrastText',
      borderRadius: 2, p: 1.5, maxWidth: '80%', borderTopRightRadius: 4,
      boxShadow: '0 2px 6px rgba(25,118,210,0.18)',
      position: 'relative',
      '&:hover .edit-btn': { opacity: 1 },
    }}>
      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{children}</Typography>
      {onEdit && (
        <IconButton className="edit-btn" size="small" onClick={onEdit}
                    sx={{
                      opacity: 0, transition: 'opacity .15s',
                      position: 'absolute', top: -10, left: -10,
                      bgcolor: 'background.paper', boxShadow: 1,
                      width: 22, height: 22,
                      '&:hover': { bgcolor: 'background.paper' },
                    }}>
          <Icon icon={editFill} width={12} />
        </IconButton>
      )}
    </Box>
  </Stack>
);

// ──────────────────────────────────────────────────────────────────
// QUICK-REPLY RENDERERS — one per step type
// ──────────────────────────────────────────────────────────────────
const TextReply = ({ step, onSubmit }) => {
  const [val, setVal] = useState('');
  return (
    <Box>
      <TextField fullWidth size="small" placeholder="Type your answer…"
                 value={val} onChange={(e) => setVal(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && val.trim() && onSubmit(val.trim())}
                 InputProps={{
                   endAdornment: (
                     <IconButton size="small" disabled={!val.trim()}
                                 onClick={() => onSubmit(val.trim())}>
                       <Icon icon={paperPlaneFill} width={16} />
                     </IconButton>
                   ),
                 }} />
      {step.suggestions?.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1, gap: 0.75 }}>
          {step.suggestions.map((s) => (
            <Chip key={s} label={s} size="small" clickable variant="outlined"
                  onClick={() => onSubmit(s)} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const NameWithNoteReply = ({ step, onSubmit }) => {
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const submit = () => name.trim() && onSubmit({ name: name.trim(), description: note.trim() });
  return (
    <Box>
      <TextField fullWidth size="small" placeholder="Campaign name…"
                 value={name} onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter' && !showNote) submit(); }} />
      {showNote ? (
        <TextField fullWidth size="small" multiline minRows={2}
                   placeholder="Optional note for the team — e.g. 'Follow-up to Q2 webinar leads'"
                   value={note} onChange={(e) => setNote(e.target.value)}
                   sx={{ mt: 1 }} />
      ) : (
        <Button size="small" sx={{ mt: 0.25 }} onClick={() => setShowNote(true)}>
          + Add a note (optional)
        </Button>
      )}
      {step.suggestions?.length > 0 && !name && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1, gap: 0.75 }}>
          {step.suggestions.map((s) => (
            <Chip key={s} label={s} size="small" clickable variant="outlined"
                  onClick={() => setName(s)} />
          ))}
        </Stack>
      )}
      <Stack direction="row" sx={{ mt: 1.25 }}>
        <Button size="small" variant="contained" disabled={!name.trim()}
                endIcon={<Icon icon={paperPlaneFill} width={14} />}
                onClick={submit}>
          Continue
        </Button>
      </Stack>
    </Box>
  );
};

const ChipsMulti = ({ step, onSubmit }) => {
  const [picked, setPicked] = useState([]);
  const toggle = (v) => setPicked((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);
  return (
    <Box>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75, mb: 1.5 }}>
        {step.options.map((o) => (
          <Chip key={o} label={o} size="small" clickable
                color={picked.includes(o) ? 'primary' : 'default'}
                variant={picked.includes(o) ? 'filled' : 'outlined'}
                onClick={() => toggle(o)} />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="contained" disabled={picked.length === 0}
                onClick={() => onSubmit(picked)}>
          Confirm {picked.length > 0 && `(${picked.length})`}
        </Button>
        {step.allowSkip && (
          <Button size="small" onClick={() => onSubmit([])}>{step.skipLabel || 'Skip'}</Button>
        )}
      </Stack>
    </Box>
  );
};

const ChipsSingle = ({ step, onSubmit }) => (
  <Box>
    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
      {step.options.map((o) => (
        <Chip key={o.k} label={o.label} size="small" clickable variant="outlined"
              onClick={() => onSubmit(o.k)} />
      ))}
      {step.allowSkip && (
        <Chip label={step.skipLabel || 'Skip'} size="small" clickable variant="outlined"
              onClick={() => onSubmit(null)} />
      )}
    </Stack>
  </Box>
);

const SizeReply = ({ onSubmit, onSkip }) => {
  const [custom, setCustom] = useState(false);
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');

  const confirmCustom = () => {
    const lo = parseInt(min, 10);
    const hi = max === '' ? 100000 : parseInt(max, 10);
    if (isNaN(lo) || lo < 0) return;
    onSubmit({ custom: true, min: lo, max: hi });
  };

  if (custom) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Enter employee count range (leave max blank for no upper limit)
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <TextField size="small" label="Min employees" type="number" sx={{ width: 140 }}
                     value={min} onChange={(e) => setMin(e.target.value)}
                     inputProps={{ min: 0 }} />
          <Typography variant="body2" color="text.secondary">to</Typography>
          <TextField size="small" label="Max employees" type="number" sx={{ width: 140 }}
                     value={max} onChange={(e) => setMax(e.target.value)}
                     placeholder="no limit" inputProps={{ min: 0 }} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained"
                  disabled={min === '' || isNaN(parseInt(min, 10))}
                  onClick={confirmCustom}>
            Confirm range
          </Button>
          <Button size="small" onClick={() => setCustom(false)}>← Back</Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
      {SIZE_OPTIONS.map((s) => (
        <Chip key={s.k} label={s.label} size="small" clickable variant="outlined"
              onClick={() => onSubmit(s.k)} />
      ))}
      <Chip label="Custom range…" size="small" clickable variant="outlined"
            onClick={() => setCustom(true)} />
      <Chip label="Any size" size="small" clickable variant="outlined"
            onClick={() => onSkip()} />
    </Stack>
  );
};

const PresetCardReply = ({ step, onSubmit }) => (
  <Stack spacing={1}>
    {step.options.map((o) => (
      <Card key={o.k} variant="outlined" onClick={() => onSubmit(o.k)}
            sx={{
              p: 1.5, cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.lighter' },
            }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Stack direction="row" spacing={0.25}>
            {o.steps.map((s, i) => (
              <Icon key={i}
                    icon={s.channel?.startsWith('LINKEDIN') ? linkedinFill : emailFill}
                    width={14}
                    color={s.channel?.startsWith('LINKEDIN') ? '#0A66C2' : '#1976d2'} />
            ))}
          </Stack>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.label}</Typography>
            <Typography variant="caption" color="text.secondary">{o.description}</Typography>
          </Box>
        </Stack>
      </Card>
    ))}
  </Stack>
);

const PresetChipReply = ({ step, onSubmit }) => (
  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
    {step.options.map((o) => (
      <Chip key={o.k} label={o.label} size="small" clickable variant="outlined"
            onClick={() => onSubmit(o.k)} />
    ))}
  </Stack>
);

const LlmFilterPanel = ({ existingKeys, onAdd, onCancel }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { rules, explanation }
  const [picked, setPicked] = useState({});

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await parseRulesFromText(text.trim());
      // Backend returns { field, operator, value_json, chipLabel } flat;
      // normalise to { rule: { field, operator, value }, chipLabel } used throughout this component.
      const normalized = (data.rules || []).map((r) => ({
        rule: { field: r.field, operator: r.operator, value: r.value_json },
        chipLabel: r.chipLabel || `${r.field} ${r.operator} ${JSON.stringify(r.value_json)}`,
      }));
      const initialPicked = {};
      normalized.forEach((_, i) => { initialPicked[i] = true; });
      setResult({ rules: normalized, explanation: data.explanation || '' });
      setPicked(initialPicked);
    } catch (err) {
      setResult({ rules: [], explanation: 'Failed to parse rules. Please try again.' });
    }
    setBusy(false);
  };

  const addAll = () => {
    const newOnes = result.rules
      .filter((_, i) => picked[i])
      .map((r) => ({
        filter_key: `llm:${r.rule.field}:${r.rule.operator}:${JSON.stringify(r.rule.value)}`,
        option_key: 'llm',
        label: r.chipLabel,
        rule: r.rule,
      }))
      .filter((f) => !existingKeys.includes(f.filter_key));
    onAdd(newOnes);
  };

  return (
    <Box sx={{ p: 1.5, bgcolor: 'primary.lighter', borderRadius: 1,
               border: 1, borderColor: 'primary.light' }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <Icon icon={flashFill} width={14} color="#1976d2" />
        <Typography variant="caption" color="primary.dark" sx={{ fontWeight: 700 }}>
          DESCRIBE IN PLAIN ENGLISH
        </Typography>
      </Stack>

      {!result && (
        <>
          <TextField
            fullWidth multiline minRows={2} size="small"
            placeholder='e.g. "SaaS companies in the US with more than 100 employees, founded after 2018, that have an email"'
            value={text} onChange={(e) => setText(e.target.value)}
            disabled={busy}
            sx={{ bgcolor: 'background.paper' }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" variant="contained" disabled={!text.trim() || busy}
                    startIcon={busy ? <CircularProgress size={12} /> : <Icon icon={paperPlaneFill} width={14} />}
                    onClick={submit}>
              {busy ? 'Thinking…' : 'Parse rules'}
            </Button>
            <Button size="small" onClick={onCancel}>Cancel</Button>
          </Stack>
        </>
      )}

      {result && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {result.explanation}
          </Typography>
          {result.rules.length > 0 && (
            <Stack spacing={0.5} sx={{ mb: 1.5 }}>
              {result.rules.map((r, i) => {
                const dup = existingKeys.includes(
                  `llm:${r.rule.field}:${r.rule.operator}:${JSON.stringify(r.rule.value)}`
                );
                return (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}
                         sx={{ p: 0.75, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Chip
                      size="small"
                      icon={picked[i] && !dup ? <Icon icon={checkmarkCircle} width={14} /> : null}
                      label={r.chipLabel}
                      color={picked[i] && !dup ? 'primary' : 'default'}
                      variant={picked[i] && !dup ? 'filled' : 'outlined'}
                      onClick={() => !dup && setPicked((p) => ({ ...p, [i]: !p[i] }))}
                      sx={{ cursor: dup ? 'not-allowed' : 'pointer' }}
                    />
                    {dup && (
                      <Typography variant="caption" color="text.secondary">
                        already added
                      </Typography>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained"
                    disabled={result.rules.length === 0 ||
                              !Object.values(picked).some(Boolean)}
                    onClick={addAll}>
              Add selected ({Object.values(picked).filter(Boolean).length})
            </Button>
            <Button size="small" onClick={() => { setResult(null); setText(''); }}>
              Try again
            </Button>
            <Button size="small" onClick={onCancel}>Cancel</Button>
          </Stack>
        </>
      )}
    </Box>
  );
};

const channelMeta = (k) => {
  if (k === 'EMAIL')           return { l: 'Email',            color: '#1976d2', icon: emailFill    };
  if (k === 'LINKEDIN_INVITE') return { l: 'LinkedIn invite',  color: '#0A66C2', icon: linkedinFill };
  if (k === 'LINKEDIN_MESSAGE')return { l: 'LinkedIn message', color: '#0A66C2', icon: linkedinFill };
  return { l: k, color: '#666', icon: emailFill };
};

const fmtDelay = (sec) => {
  if (!sec || sec <= 0) return 'Send immediately';
  const d = Math.round(sec / 86400);
  if (d >= 1) return `Wait ${d} day${d > 1 ? 's' : ''}`;
  const h = Math.round(sec / 3600);
  return `Wait ${h}h`;
};

const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const CustomizeStepsReply = ({ steps, templates, onChange, onContinue, minStepGapSeconds = 30 }) => {
  const [editing, setEditing] = useState({ tplIdx: -1, delayIdx: -1 });

  const updateStep = (idx, patch) => {
    const next = [...steps];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const setDelaySeconds = (idx, secs) => {
    const safe = Math.max(0, Math.max(secs === 0 ? 0 : minStepGapSeconds, secs));
    updateStep(idx, { delay_seconds: safe });
  };

  return (
    <Box>
      <Stack spacing={1}>
        {steps.map((s, i) => {
          const meta = channelMeta(s.channel);
          const tpl = templates.find((t) => t.id === s.message_template_id);
          const channelTpls = templates.filter((t) => t.channel === s.channel);
          const tplOpen = editing.tplIdx === i;
          const delayOpen = editing.delayIdx === i;

          return (
            <Card key={i} variant="outlined"
                  sx={{ borderLeft: 4, borderLeftColor: meta.color, p: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                <Icon icon={meta.icon} color={meta.color} width={14} />
                <Typography variant="caption" sx={{ color: meta.color, fontWeight: 700, letterSpacing: 0.6, fontSize: 10 }}>
                  STEP {i + 1} · {meta.l.toUpperCase()}
                </Typography>
              </Stack>

              {/* Template row */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {tpl?.name || 'No template'}
                  </Typography>
                  {tpl?.subject && (
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: 'block', whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis', overflow: 'hidden' }}>
                      Subject: {tpl.subject}
                    </Typography>
                  )}
                </Box>
                <Button size="small" variant={tplOpen ? 'contained' : 'outlined'}
                        onClick={() => setEditing((e) => ({ ...e, tplIdx: tplOpen ? -1 : i }))}>
                  {tplOpen ? 'Done' : 'Change'}
                </Button>
              </Stack>

              {tplOpen && (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {channelTpls.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No templates available for {meta.l.toLowerCase()}.
                    </Typography>
                  ) : channelTpls.map((t) => {
                    const sel = t.id === s.message_template_id;
                    return (
                      <Card key={t.id} variant="outlined"
                            onClick={() => updateStep(i, { message_template_id: t.id })}
                            sx={{
                              p: 1, cursor: 'pointer',
                              borderColor: sel ? 'primary.main' : 'grey.300',
                              bgcolor: sel ? 'primary.lighter' : 'background.paper',
                            }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {sel && <Icon icon={checkmarkCircle} color="#1976d2" width={14} />}
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                        </Stack>
                        {t.subject && (
                          <Typography variant="caption" color="text.secondary"
                                      sx={{ display: 'block', mt: 0.25 }}>
                            Subject: {t.subject}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary"
                                    sx={{ display: '-webkit-box', WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.25 }}>
                          {stripHtml(t.body_html)}
                        </Typography>
                      </Card>
                    );
                  })}
                </Stack>
              )}

              {/* Delay row */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
                  <Icon icon={clockFill} width={13} color="#666" />
                  <Typography variant="caption" color="text.secondary">
                    {fmtDelay(s.delay_seconds)}
                  </Typography>
                </Stack>
                <Button size="small" variant={delayOpen ? 'contained' : 'outlined'}
                        onClick={() => setEditing((e) => ({ ...e, delayIdx: delayOpen ? -1 : i }))}>
                  {delayOpen ? 'Done' : 'Edit delay'}
                </Button>
              </Stack>

              {delayOpen && (
                <Box sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, mb: 1 }}>
                    {[0, 1, 2, 3, 5, 7, 14].map((d) => (
                      <Chip key={d} size="small"
                            label={d === 0 ? 'Immediate' : `${d} day${d > 1 ? 's' : ''}`}
                            color={s.delay_seconds === d * 86400 ? 'primary' : 'default'}
                            variant={s.delay_seconds === d * 86400 ? 'filled' : 'outlined'}
                            clickable
                            onClick={() => setDelaySeconds(i, d * 86400)} />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField size="small" type="number" sx={{ width: 90 }}
                               value={Math.max(0, Math.round((s.delay_seconds || 0) / 86400))}
                               onChange={(e) => setDelaySeconds(i, Number(e.target.value) * 86400)} />
                    <Typography variant="caption" color="text.secondary">days from previous step</Typography>
                  </Stack>
                  {i === 0 && s.delay_seconds > 0 && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                      Step 1 with a delay means companies wait before the first message.
                    </Typography>
                  )}
                </Box>
              )}
            </Card>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button size="small" variant="contained" onClick={onContinue}>
          Looks good — continue
        </Button>
        <Button size="small" onClick={onContinue}>
          Skip — defaults are fine
        </Button>
      </Stack>
    </Box>
  );
};

const ScheduleReply = ({ onSubmit, customConfig, onCustomChange }) => {
  const [mode, setMode] = useState(null);   // null = picker; 'custom' = inline form
  if (mode === 'custom') {
    const cfg = customConfig;
    const update = (patch) => onCustomChange({ ...cfg, ...patch });
    return (
      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.300' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Custom schedule
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
          <Box sx={{ minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary">Start</Typography>
            <TextField type="time" size="small" fullWidth
                       value={cfg.window_start}
                       onChange={(e) => update({ window_start: e.target.value })} />
          </Box>
          <Box sx={{ minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary">End</Typography>
            <TextField type="time" size="small" fullWidth
                       value={cfg.window_end}
                       onChange={(e) => update({ window_end: e.target.value })} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography variant="caption" color="text.secondary">Timezone</Typography>
            <Select size="small" fullWidth value={cfg.timezone}
                    onChange={(e) => update({ timezone: e.target.value })}>
              {['Asia/Kolkata', 'Asia/Singapore', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'UTC']
                .map((tz) => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
            </Select>
          </Box>
        </Stack>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Weekdays
          </Typography>
          <ToggleButtonGroup size="small" value={cfg.weekdays}
                             onChange={(_, next) => update({ weekdays: next })}
                             sx={{ flexWrap: 'wrap' }}>
            {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d) => (
              <ToggleButton key={d} value={d} sx={{ flex: 1, minWidth: 52 }}>{d}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Per-BA daily cap</Typography>
            <TextField type="number" size="small" fullWidth
                       value={cfg.per_pm_daily_cap}
                       onChange={(e) => update({ per_pm_daily_cap: Number(e.target.value) })} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Total cap (0 = unlimited)</Typography>
            <TextField type="number" size="small" fullWidth
                       value={cfg.daily_send_cap || 0}
                       onChange={(e) => update({ daily_send_cap: Number(e.target.value) })} />
          </Box>
        </Stack>
        <FormControlLabel
          control={<Switch checked={!!cfg.respect_recipient_tz}
                           onChange={(e) => update({ respect_recipient_tz: e.target.checked })} />}
          label={<Typography variant="caption">Respect recipient timezone (delay sends until their business hours)</Typography>}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
          <Button size="small" variant="contained"
                  disabled={!cfg.weekdays?.length}
                  onClick={() => onSubmit({ kind: 'custom' })}>
            Save schedule
          </Button>
          <Button size="small" onClick={() => setMode(null)}>Back to presets</Button>
        </Stack>
      </Box>
    );
  }
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
      {SCHEDULE_PRESETS.map((o) => (
        <Chip key={o.k} label={o.label} size="small" clickable variant="outlined"
              onClick={() => onSubmit({ kind: 'preset', key: o.k })} />
      ))}
      <Chip label="Custom…" size="small" clickable color="primary" variant="outlined"
            icon={<Icon icon={editFill} width={12} />}
            onClick={() => setMode('custom')} />
    </Stack>
  );
};

const MoreFiltersReply = ({ filters, onChange, onDone }) => {
  const [subStep, setSubStep] = useState(null);  // filter key OR 'llm' OR null
  const usedKeys = filters.map((f) => f.filter_key);
  const available = EXTRA_FILTERS.filter((f) => !usedKeys.includes(f.k));

  const removeFilter = (key) => {
    onChange(filters.filter((f) => f.filter_key !== key));
  };
  const addFilter = (filterKey, option) => {
    onChange([...filters, {
      filter_key: filterKey,
      option_key: option.k,
      label: `${EXTRA_FILTERS.find((x) => x.k === filterKey).chipLabel}: ${option.label}`,
      rule: option.rule,
    }]);
    setSubStep(null);
  };
  const addLlmFilters = (newFilters) => {
    onChange([...filters, ...newFilters]);
    setSubStep(null);
  };

  const activeFilter = subStep && subStep !== 'llm'
    ? EXTRA_FILTERS.find((f) => f.k === subStep) : null;

  return (
    <Box>
      {filters.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1.5, gap: 0.5 }}>
          {filters.map((f) => (
            <Chip key={f.filter_key} label={f.label} size="small"
                  color="primary" variant="outlined"
                  onDelete={() => removeFilter(f.filter_key)} />
          ))}
        </Stack>
      )}

      {subStep === 'llm' ? (
        <LlmFilterPanel
          existingKeys={usedKeys}
          onAdd={addLlmFilters}
          onCancel={() => setSubStep(null)} />
      ) : activeFilter ? (
        <Box sx={{ p: 1.25, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.300' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {activeFilter.q}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
            {activeFilter.options.map((o) => (
              <Chip key={o.k} label={o.label} size="small" clickable variant="outlined"
                    onClick={() => addFilter(activeFilter.k, o)} />
            ))}
            <Chip label="cancel" size="small" clickable
                  onClick={() => setSubStep(null)} />
          </Stack>
        </Box>
      ) : (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
          <Chip
            icon={<Icon icon={flashFill} width={14} />}
            label="Describe in plain English"
            size="small" clickable
            color="primary" variant="outlined"
            onClick={() => setSubStep('llm')}
          />
          {available.map((f) => (
            <Chip key={f.k} label={`+ ${f.label}`} size="small" clickable variant="outlined"
                  onClick={() => setSubStep(f.k)} />
          ))}
          <Chip
            label={filters.length === 0 ? "Skip — that's enough" : 'Done — continue'}
            size="small" color="primary" clickable
            onClick={() => onDone(filters)}
          />
        </Stack>
      )}
    </Box>
  );
};

const RichChipsMulti = ({ items, onSubmit, multi = true, emptyHint, validate, ctaLabel }) => {
  const [picked, setPicked] = useState([]);
  const toggle = (v) => {
    if (!multi) { setPicked([v]); return; }
    setPicked((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);
  };
  const ok = validate ? validate(picked) : picked.length > 0;
  return (
    <Box>
      <Stack spacing={0.75} sx={{ mb: 1.5 }}>
        {items.map((it) => {
          const isPicked = picked.includes(it.value);
          return (
            <Card key={it.value} variant="outlined"
                  onClick={() => !it.disabled && toggle(it.value)}
                  sx={{
                    p: 1.25, cursor: it.disabled ? 'not-allowed' : 'pointer',
                    bgcolor: isPicked ? 'primary.lighter' : 'background.paper',
                    borderColor: isPicked ? 'primary.main' : 'grey.300',
                    opacity: it.disabled ? 0.6 : 1,
                  }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {it.icon && (
                  <Icon icon={it.icon} width={16} color={it.iconColor || undefined} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.label}</Typography>
                  {it.subLabel && (
                    <Typography variant="caption" color="text.secondary">{it.subLabel}</Typography>
                  )}
                </Box>
                {it.right && (
                  <Typography variant="caption" color={it.rightColor || 'text.secondary'}>
                    {it.right}
                  </Typography>
                )}
              </Stack>
            </Card>
          );
        })}
      </Stack>
      {items.length === 0 && emptyHint && (
        <Alert severity="warning" sx={{ mb: 1 }}>{emptyHint}</Alert>
      )}
      <Button size="small" variant="contained" disabled={!ok}
              onClick={() => onSubmit(picked)}>
        {ctaLabel || `Confirm${picked.length > 0 ? ` (${picked.length})` : ''}`}
      </Button>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────
// REVIEW STEP — final summary card
// ──────────────────────────────────────────────────────────────────
const ReviewCard = ({
  answers, audience, busy, templates, outlook, unipile,
  onLaunch, onBack,
}) => {
  const seq = SEQUENCE_PRESETS.find((s) => s.k === answers.sequence);
  const steps = answers.steps || seq?.steps || [];
  const tplName = (id) => templates.find((t) => t.id === id)?.name || '—';

  const schedKind = answers.schedule_kind;
  const sched = schedKind === 'custom'
    ? { label: 'Custom schedule', config: answers.custom_schedule }
    : SCHEDULE_PRESETS.find((s) => s.k === answers.schedule);

  const pmDetails = (answers.pms || []).map((id) => outlook.find((o) => o.user.id === id)).filter(Boolean);
  const liDetails = (answers.linkedin || []).map((id) => unipile.find((u) => u.id === id)).filter(Boolean);

  return (
    <Card variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
      <Box sx={{ p: 2, bgcolor: 'primary.lighter', borderBottom: 1, borderColor: 'primary.light' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.dark', letterSpacing: 0.7 }}>
          REVIEW & LAUNCH
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>
          {answers.name || '— unnamed campaign —'}
        </Typography>
        {answers.description && (
          <Typography variant="caption" color="text.secondary">{answers.description}</Typography>
        )}
      </Box>

      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* AUDIENCE */}
          <Box>
            <Typography variant="caption" color="text.secondary"
                        sx={{ fontWeight: 600, letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
              AUDIENCE
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {audience.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                match now · auto-grows ↻
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75, gap: 0.5 }}>
              {(answers.industry || []).map((v) => <Chip key={v} size="small" label={v} />)}
              {(answers.country  || []).map((v) => <Chip key={v} size="small" label={v} />)}
              {answers.size && (
                <Chip size="small" label={
                  answers.size?.custom
                    ? `${answers.size.min || 0}–${answers.size.max || '∞'} employees`
                    : SIZE_OPTIONS.find((s) => s.k === answers.size)?.label
                } />
              )}
              {(answers.extra_filters || []).map((f) => (
                <Chip key={f.filter_key} size="small" variant="outlined" color="primary" label={f.label} />
              ))}
            </Stack>
          </Box>

          {/* SEQUENCE */}
          <Box>
            <Typography variant="caption" color="text.secondary"
                        sx={{ fontWeight: 600, letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
              SEQUENCE · {steps.length} STEP{steps.length === 1 ? '' : 'S'}
            </Typography>
            <Stack spacing={0.75}>
              {steps.map((s, i) => {
                const meta = channelMeta(s.channel);
                return (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}
                         sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1,
                               borderLeft: 3, borderLeftColor: meta.color }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%',
                               bgcolor: `${meta.color}18`,
                               display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon icon={meta.icon} color={meta.color} width={12} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: meta.color, fontWeight: 700, fontSize: 9.5 }}>
                        STEP {i + 1} · {meta.l.toUpperCase()}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}
                                  noWrap>
                        {tplName(s.message_template_id)}
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={0.4}
                           sx={{ color: 'text.secondary' }}>
                      <Icon icon={clockFill} width={11} />
                      <Typography variant="caption">{fmtDelay(s.delay_seconds)}</Typography>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          {/* SENDERS */}
          <Box>
            <Typography variant="caption" color="text.secondary"
                        sx={{ fontWeight: 600, letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
              SENDERS
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
              <Box sx={{ flex: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Icon icon={emailFill} color="#1976d2" width={13} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10 }}>EMAIL · ROUND-ROBIN</Typography>
                </Stack>
                {pmDetails.length === 0 ? (
                  <Typography variant="caption" color="error.main">No BA picked</Typography>
                ) : (
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {pmDetails.map((o) => (
                      <Typography key={o.user.id} variant="caption" sx={{ display: 'block' }}>
                        · <b>{o.user.name}</b> ({o.email})
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>
              <Box sx={{ flex: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Icon icon={linkedinFill} color="#0A66C2" width={13} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10 }}>
                    LINKEDIN POOL{liDetails.length > 1 ? ' · ROUND-ROBIN' : ''}
                  </Typography>
                </Stack>
                {liDetails.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {steps.some((s) => s.channel?.startsWith('LINKEDIN')) ? 'No profile picked' : 'Not used'}
                  </Typography>
                ) : (
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {liDetails.map((u) => (
                      <Typography key={u.id} variant="caption" sx={{ display: 'block' }}>
                        · <b>{u.display_name}</b> (@{u.linkedin_handle})
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>

          {/* SCHEDULE */}
          <Box>
            <Typography variant="caption" color="text.secondary"
                        sx={{ fontWeight: 600, letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
              SCHEDULE
            </Typography>
            <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{sched?.label || '—'}</Typography>
              {schedKind === 'custom' && answers.custom_schedule && (
                <Typography variant="caption" color="text.secondary">
                  {answers.custom_schedule.weekdays?.join(', ')} · {answers.custom_schedule.window_start}–{answers.custom_schedule.window_end} {answers.custom_schedule.timezone} · {answers.custom_schedule.per_pm_daily_cap}/BA/day
                  {answers.custom_schedule.respect_recipient_tz && ' · respects recipient TZ'}
                </Typography>
              )}
              {schedKind !== 'custom' && sched?.schedule && (
                <Typography variant="caption" color="text.secondary">
                  {sched.schedule.weekdays?.join(', ')} · {sched.schedule.window_start}–{sched.schedule.window_end} {sched.schedule.timezone} · {sched.schedule.per_pm_daily_cap}/BA/day
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<Icon icon={arrowBackFill} />} onClick={onBack}>
            Back
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" size="medium" disabled={busy}
                  startIcon={busy ? <CircularProgress size={14} /> : <Icon icon={paperPlaneFill} />}
                  onClick={onLaunch}>
            Launch campaign
          </Button>
        </Stack>
      </Box>
    </Card>
  );
};

// ──────────────────────────────────────────────────────────────────
// MAIN — chat drawer
// ──────────────────────────────────────────────────────────────────
const ChatCampaignWizard = ({ open, onClose, onLaunched }) => {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [audience, setAudience] = useState(0);
  const [outlook, setOutlook] = useState([]);
  const [unipile, setUnipile] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ countries: FALLBACK_COUNTRY_OPTIONS, industries: FALLBACK_INDUSTRY_OPTIONS });
  const [busy, setBusy] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const scrollRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStepIdx(0);
      setAnswers({
        custom_schedule: {
          window_start: '09:00', window_end: '18:00', timezone: 'Asia/Kolkata',
          weekdays: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
          per_pm_daily_cap: 50, daily_send_cap: 0, respect_recipient_tz: false,
        },
      });
      setAudience(0);
      Promise.allSettled([listOutlookAccounts(), listUnipileAccounts(), listMessageTemplates(), getFilterOptions()])
        .then(([o, u, t, f]) => {
          if (o.status === 'fulfilled') setOutlook(o.value.data || []);
          if (u.status === 'fulfilled') setUnipile(u.value.data || []);
          if (t.status === 'fulfilled') setTemplates(t.value.data || []);
          if (f.status === 'fulfilled') {
            const { countries, industries } = f.value.data || {};
            setFilterOptions({
              countries:  countries?.length  ? countries  : FALLBACK_COUNTRY_OPTIONS,
              industries: industries?.length ? industries : FALLBACK_INDUSTRY_OPTIONS,
            });
          }
        });
    }
  }, [open]);

  // Auto-scroll on new message
  useEffect(() => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  }, [stepIdx, answers]);

  // Live audience count when audience answers change
  useEffect(() => {
    const rules = buildRules(answers);
    if (rules.length === 0) { setAudience(0); return; }
    previewEligibility(rules).then(({ data }) => setAudience(data.count));
  }, [answers.industry, answers.country, answers.size]);

  // Customize step appears only after a sequence preset is picked.
  // LinkedIn step appears only if the sequence has a LinkedIn channel step.
  const visibleSteps = useMemo(() => {
    const stepsForChannelCheck = answers.steps && answers.steps.length
      ? answers.steps
      : SEQUENCE_PRESETS.find((s) => s.k === answers.sequence)?.steps || [];
    const hasLi = !!stepsForChannelCheck.some((s) => s.channel?.startsWith('LINKEDIN'));
    return STEPS.filter((s) => {
      if (s.id === 'customize') return !!answers.sequence;
      if (s.onlyIfLinkedinSequence) return hasLi;
      return true;
    });
  }, [answers.sequence, answers.steps]);

  const recordAnswer = (id, value) => {
    setAnswers((a) => {
      const next = { ...a, [id]: value };
      // When a sequence preset is picked, populate steps from it (if not already customized)
      if (id === 'sequence') {
        const seq = SEQUENCE_PRESETS.find((s) => s.k === value);
        if (seq) next.steps = seq.steps.map((s, i) => ({
          ...s, order: i + 1,
          stop_on_reply: true,
          skip_if_no_email: s.channel === 'EMAIL',
          skip_if_no_linkedin: s.channel?.startsWith('LINKEDIN'),
        }));
      }
      return next;
    });
    const idxInVisible = visibleSteps.findIndex((s) => s.id === id);
    setStepIdx(Math.min(idxInVisible + 1, visibleSteps.length));
  };

  const editFrom = (id) => {
    const idx = visibleSteps.findIndex((s) => s.id === id);
    setStepIdx(idx);
    setAnswers((prev) => {
      const next = { ...prev };
      visibleSteps.slice(idx).forEach((s) => { delete next[s.id]; });
      // re-derive: if going back to or before sequence, clear derived steps
      if (id === 'sequence' || id === 'customize') delete next.steps;
      if (id === 'schedule') { delete next.schedule_kind; }
      return next;
    });
  };

  // Build rich items for sender steps
  const pmItems = outlook.map((o) => ({
    value: o.user.id,
    label: o.user.name,
    subLabel: o.email,
    icon: emailFill, iconColor: '#1976d2',
    right: o.health === 'token_expired' ? 'token expired' : `${o.sent_today} sent today`,
    rightColor: o.health === 'token_expired' ? 'error.main' : 'text.secondary',
    disabled: o.health === 'token_expired',
  }));
  const liItems = unipile.map((u) => ({
    value: u.id,
    label: u.display_name,
    subLabel: `@${u.linkedin_handle} · ${u.profile_name}`,
    icon: linkedinFill, iconColor: '#0A66C2',
    right: `${u.actions_today}/${u.daily_action_cap}`,
    rightColor: u.actions_today >= u.daily_action_cap * 0.9 ? 'warning.main' : 'text.secondary',
  }));

  const launch = async () => {
    setBusy(true);
    try {
    const presetSeq = SEQUENCE_PRESETS.find((s) => s.k === answers.sequence);
    const presetSched = SCHEDULE_PRESETS.find((s) => s.k === answers.schedule);
    const useCustomSched = answers.schedule_kind === 'custom';
    const sched = useCustomSched ? answers.custom_schedule : presetSched?.schedule;

    const finalSteps = (answers.steps && answers.steps.length
      ? answers.steps
      : (presetSeq?.steps || [])
    ).map((s, i) => ({
      ...s, order: i + 1,
      stop_on_reply: s.stop_on_reply ?? true,
      skip_if_no_email:    s.skip_if_no_email    ?? (s.channel === 'EMAIL'),
      skip_if_no_linkedin: s.skip_if_no_linkedin ?? s.channel?.startsWith('LINKEDIN'),
    }));

    const draft = {
      name: answers.name || 'Untitled campaign',
      description: answers.description || '',
      rules: buildRules(answers),
      pm_assignments: (answers.pms || [])
        .map(userId => outlook.find((x) => x.user.id === userId))
        .filter((o) => o && o.id)
        .map((o) => ({
          user: o.user.id,
          outlook_connection: o.id,
          unipile_account: (answers.linkedin && answers.linkedin.length) ? answers.linkedin[0] : null,
          weight: 1,
          is_active: true,
        })),
      steps: finalSteps,
      schedule_window_start: sched?.window_start || '09:00',
      schedule_window_end: sched?.window_end || '18:00',
      schedule_timezone: sched?.timezone || 'Asia/Kolkata',
      schedule_weekdays: sched?.weekdays || ['MON','TUE','WED','THU','FRI'],
      per_pm_daily_cap: sched?.per_pm_daily_cap || 50,
      daily_send_cap: sched?.daily_send_cap || 0,
      min_step_gap_seconds: sched?.min_step_gap_seconds || 30,
    };
    const res = await createCampaign(draft);
    const activateRes = await activateCampaign(res.data.id);
    setBusy(false);
    onLaunched?.(activateRes.data || res.data);
    onClose();
    navigate(`/outreach/campaigns/${res.data.id}`);
    } catch (err) {
      setBusy(false);
      const msg = err?.response?.data?.detail || err?.message || 'Launch failed';
      setLaunchError(msg);
    }
  };

  const progress = Math.min((stepIdx / Math.max(visibleSteps.length - 1, 1)) * 100, 100);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 720, lg: 820 } } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%',
                 bgcolor: 'grey.50' }}>
        {/* Header */}
        <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ px: 3, py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar sx={{
                width: 40, height: 40,
                bgcolor: 'primary.main',
                boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
              }}>
                <Icon icon={flashFill} width={22} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Campaign assistant
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Answer a few questions — I'll set up the campaign for you
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">
                  Step {Math.min(stepIdx + 1, visibleSteps.length)} of {visibleSteps.length}
                </Typography>
              </Box>
              <IconButton onClick={onClose}><Icon icon={closeFill} /></IconButton>
            </Stack>
          </Box>
          <Box sx={{ height: 3, bgcolor: 'grey.200' }}>
            <Box sx={{
              height: '100%', bgcolor: 'primary.main',
              width: `${progress}%`,
              transition: 'width 0.3s ease',
            }} />
          </Box>
        </Box>

        {/* Conversation */}
        <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
          {visibleSteps.slice(0, stepIdx + 1).map((step, i) => {
            const isCurrent = i === stepIdx;
            const isAnswered = answers[step.id] !== undefined;
            return (
              <Box key={step.id}>
                <BotBubble>
                  {step.id === 'review'
                    ? "Here's what I have — ready to launch?"
                    : step.q}
                </BotBubble>

                {isAnswered && (
                  <UserBubble onEdit={isCurrent ? null : () => editFrom(step.id)}>
                    {step.id === 'name'
                      ? (answers.description
                          ? `${answers.name} · ${answers.description}`
                          : answers.name)
                      : step.id === 'pms'
                      ? `${(answers.pms || []).length} BA${(answers.pms || []).length !== 1 ? 's' : ''}`
                      : step.id === 'linkedin'
                      ? `${(answers.linkedin || []).length} LinkedIn profile${(answers.linkedin || []).length !== 1 ? 's' : ''}`
                      : step.id === 'more' && (answers.extra_filters || []).length > 0
                      ? (answers.extra_filters || []).map((f) => f.label).join(' · ')
                      : step.id === 'customize'
                      ? `${(answers.steps || []).length} step${(answers.steps || []).length !== 1 ? 's' : ''} configured`
                      : step.id === 'schedule' && answers.schedule_kind === 'custom'
                      ? `Custom · ${answers.custom_schedule?.weekdays?.join(',')} ${answers.custom_schedule?.window_start}–${answers.custom_schedule?.window_end} ${answers.custom_schedule?.timezone}`
                      : summarizeAnswer(step, answers[step.id])}
                  </UserBubble>
                )}

                {isCurrent && !isAnswered && (
                  <Box sx={{ pl: 6, mb: 2 }}>
                    {step.type === 'text' && step.id === 'name' && (
                      <NameWithNoteReply step={step} onSubmit={(vals) => {
                        setAnswers((a) => ({ ...a, name: vals.name, description: vals.description }));
                        const idx = visibleSteps.findIndex((s) => s.id === 'name');
                        setStepIdx(Math.min(idx + 1, visibleSteps.length));
                      }} />
                    )}
                    {step.type === 'text' && step.id !== 'name' && (
                      <TextReply step={step} onSubmit={(v) => recordAnswer(step.id, v)} />
                    )}
                    {step.type === 'chips-multi' && (
                      <ChipsMulti
                        step={{
                          ...step,
                          options: step.id === 'industry' ? filterOptions.industries
                                 : step.id === 'country'  ? filterOptions.countries
                                 : step.options,
                        }}
                        onSubmit={(v) => recordAnswer(step.id, v)} />
                    )}
                    {step.type === 'chips-single' && (
                      <ChipsSingle step={step} onSubmit={(v) => recordAnswer(step.id, v)} />
                    )}
                    {step.type === 'size-picker' && (
                      <SizeReply onSubmit={(v) => recordAnswer(step.id, v)}
                                 onSkip={() => recordAnswer(step.id, null)} />
                    )}
                    {step.type === 'preset-card' && (
                      <PresetCardReply step={step} onSubmit={(v) => recordAnswer(step.id, v)} />
                    )}
                    {step.type === 'customize' && (
                      <CustomizeStepsReply
                        steps={answers.steps || []}
                        templates={templates}
                        onChange={(next) => setAnswers((a) => ({ ...a, steps: next }))}
                        onContinue={() => recordAnswer('customize', true)} />
                    )}
                    {step.type === 'schedule' && (
                      <ScheduleReply
                        customConfig={answers.custom_schedule}
                        onCustomChange={(cfg) => setAnswers((a) => ({ ...a, custom_schedule: cfg }))}
                        onSubmit={(picked) => {
                          if (picked.kind === 'custom') {
                            setAnswers((a) => ({ ...a, schedule: 'custom', schedule_kind: 'custom' }));
                          } else {
                            setAnswers((a) => ({ ...a, schedule: picked.key, schedule_kind: 'preset' }));
                          }
                          const idx = visibleSteps.findIndex((s) => s.id === 'schedule');
                          setStepIdx(Math.min(idx + 1, visibleSteps.length));
                        }} />
                    )}
                    {step.type === 'more-filters' && (
                      <MoreFiltersReply
                        filters={answers.extra_filters || []}
                        onChange={(next) => setAnswers((a) => ({ ...a, extra_filters: next }))}
                        onDone={(filters) => recordAnswer('more', filters)} />
                    )}
                    {step.type === 'chips-multi-rich' && step.id === 'pms' && (
                      <RichChipsMulti
                        items={pmItems}
                        emptyHint="No connected Outlook accounts."
                        onSubmit={(v) => recordAnswer('pms', v)} />
                    )}
                    {step.type === 'chips-multi-rich' && step.id === 'linkedin' && (
                      <RichChipsMulti
                        items={liItems}
                        emptyHint="No LinkedIn profiles in the pool."
                        onSubmit={(v) => recordAnswer('linkedin', v)} />
                    )}
                  </Box>
                )}

                {step.type === 'review' && stepIdx === visibleSteps.length - 1 && (
                  <Box sx={{ pl: 6, mb: 2 }}>
                    {launchError && (
                      <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setLaunchError(null)}>
                        {launchError}
                      </Alert>
                    )}
                    <ReviewCard
                      answers={answers}
                      audience={audience}
                      busy={busy}
                      templates={templates}
                      outlook={outlook}
                      unipile={unipile}
                      onLaunch={launch}
                      onBack={() => setStepIdx(Math.max(stepIdx - 1, 0))}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Footer hint */}
        <Box sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider',
                   bgcolor: 'background.paper' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Icon icon={refreshFill} width={14} color="#1976d2" />
            <Typography variant="caption" color="text.secondary">
              Once launched, the campaign keeps enrolling new matches automatically every hour.
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ChatCampaignWizard;
